
/**
 * NBC TALK PAY API Service
 * Servicio para comunicarse con el backend NBC TALK PAY
 */

import type {
  CasinoPartner,
  CreateCasinoPartnerRequest,
  CreateCasinoPartnerResponse,
  RegenerateApiKeyResponse,
  DeletePartnerResponse,
} from '../../types/casino-partners';

// Tipos específicos para Casino Partners (legacy)
export interface UpdateCasinoPartnerRequest {
  name?: string;
  webhook_url?: string;
  is_active?: boolean;
}

export interface UpdateCasinoPartnerResponse {
  success: boolean;
  message: string;
  partner?: CasinoPartner;
  error?: string;
}

export interface GetCasinoPartnerResponse {
  success: boolean;
  partner?: CasinoPartner;
  error?: string;
}

export interface DeleteCasinoPartnerResponse {
  success: boolean;
  message: string;
  error?: string;
}

// Interface específica para la respuesta de listar casino partners
export interface ListCasinoPartnersResponse {
  success: boolean;
  partners: CasinoPartner[];
  total: number;
  error?: string;
}

// Configuración de la API
const API_CONFIG = {
  baseUrl: process.env.BASE_URL || 'http://localhost:5000',
  apiKey: process.env.API_KEY || '',
  adminApiKey: process.env.ADMIN_API_KEY || '', // Usar ADMIN_API_KEY específica
  timeout: 10000, // 10 segundos
  useProxy: process.env.NEXT_PUBLIC_USE_NBC_PROXY !== 'false', // Usar proxy por defecto (true)
};

// Tipos TypeScript
export interface NBCUser {
  success: boolean;
  message?: string;
  wallet_address?: string;
  public_key?: string;  // El backend devuelve public_key, no wallet_address
  user_data?: {
    id: number;
    name: string;
    email: string;
    nbc_talk_id: string;
  };
}

export interface APIError {
  success: false;
  error: string;
  details?: any;
}

/**
 * Realizar request HTTP al backend NBC TALK PAY
 */
async function makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Para endpoints que comienzan con /api/, usar el proxy interno de Next.js
  // Para otros endpoints, usar la URL directa del backend
  const isInternalProxy = endpoint.startsWith('/api/');
  
  // Construir URL correcta según el entorno
  let url: string;
  if (isInternalProxy) {
    // Para el proxy interno, usar la URL base de Next.js
    if (typeof window !== 'undefined') {
      // En el cliente (browser)
      url = `${window.location.origin}${endpoint}`;
    } else {
      // En el servidor (SSR/API routes)
      url = `http://localhost:3000${endpoint}`;
    }
  } else {
    // Para llamadas directas al backend
    url = `${API_CONFIG.baseUrl}${endpoint}`;
  }
  
  console.log(`🌐 [makeRequest] Starting request:`);
  console.log(`📍 [makeRequest] URL: ${url}`);
  console.log(`📋 [makeRequest] Method: ${options.method || 'GET'}`);
  console.log(`� [makeRequest] Using ${isInternalProxy ? 'internal proxy' : 'direct backend call'}`);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
    
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };
    
    // Solo establecer Content-Type: application/json si no es FormData
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Solo agregar API key para llamadas directas al backend
    if (!isInternalProxy) {
      headers['x-api-key'] = API_CONFIG.apiKey;
      console.log(`🔑 [makeRequest] API Key: ${API_CONFIG.apiKey.substring(0, 8)}...`);
    }
    
    const requestOptions: RequestInit = {
      ...options,
      signal: controller.signal,
      headers,
    };
    
    // Para llamadas internas (proxy), no necesitamos configurar CORS
    if (!isInternalProxy) {
      requestOptions.mode = 'cors';
      requestOptions.credentials = 'omit';
    }
    
    //console.log(`📤 [makeRequest] Request headers:`, requestOptions.headers);
    console.log(`📤 [makeRequest] Request body:`, options.body || 'No body');
    
    const response = await fetch(url, requestOptions);
    
    clearTimeout(timeoutId);
    
    console.log(`📡 [makeRequest] Response status: ${response.status} ${response.statusText}`);
    console.log(`📡 [makeRequest] Response headers:`, Object.fromEntries(response.headers.entries()));
    
    const responseText = await response.text();
    console.log(`📡 [makeRequest] Response body (first 500 chars):`, responseText.substring(0, 500));
    
    if (!response.ok) {
      const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      console.error(`❌ [makeRequest] HTTP Error: ${errorMessage}`);
      console.error(`❌ [makeRequest] Response body:`, responseText);
      throw new Error(errorMessage);
    }
    
    const data = responseText ? JSON.parse(responseText) : {};
    console.log(`✅ [makeRequest] Parsed response:`, data);
    return data as T;
    
  } catch (error: unknown) {
    console.error('❌ [makeRequest] Exception occurred:', error);
    
    if (error instanceof Error) {
      console.error('❌ [makeRequest] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 3).join('\n') // Solo las primeras 3 líneas del stack
      });
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - NBC TALK PAY server is not responding');
      }
      throw new Error(`Connection error: ${error.message}`);
    }
    
    throw new Error('Unknown error communicating with NBC TALK PAY');
  }
}

/**
 * Validar si un email/identificador tiene wallet en NBC TALK PAY
 */
export async function validateNBCUser(identifier: string): Promise<NBCUser> {
  console.log(`🔍 [validateNBCUser] Starting validation for: "${identifier}"`);
  
  if (!identifier || identifier.trim().length === 0) {
    const error = 'You must provide an email or identifier';
    console.error('❌ [validateNBCUser] Validation failed:', error);
    throw new Error(error);
  }

  // Validación básica de formato de email
  if (identifier.includes('@') && !isValidEmail(identifier)) {
    const error = 'The email format is not valid';
    console.error('❌ [validateNBCUser] Email format invalid:', error);
    throw new Error(error);
  }

  console.log(`🔍 [validateNBCUser] Validation passed, calling API for: ${identifier}`);
  console.log(`🔧 [validateNBCUser] API Config:`, {
    baseUrl: API_CONFIG.baseUrl,
    useProxy: API_CONFIG.useProxy,
    hasApiKey: !!API_CONFIG.apiKey,
    timeout: API_CONFIG.timeout
  });

  try {
    const requestBody = { identifier: identifier.trim() };
    console.log(`📤 [validateNBCUser] Request body:`, requestBody);
    
    let response: NBCUser;
    
    if (API_CONFIG.useProxy) {
      // Usar proxy interno de Next.js (recomendado para evitar CORS)
      console.log(`� [validateNBCUser] Using internal proxy`);
      response = await makeRequest<NBCUser>('/api/get_wallet_by_user', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
    } else {
      // Llamada directa al backend (requiere CORS configurado)
      console.log(`🌐 [validateNBCUser] Using direct backend call`);
      response = await makeRequest<NBCUser>('/get_wallet_by_user', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
    }

    console.log(`✅ [validateNBCUser] API response received:`, response);
    
    // El backend devuelve public_key cuando el usuario existe
    if (response.public_key) {
      // Normalizar la respuesta para compatibilidad
      const normalizedResponse: NBCUser = {
        success: true,
        message: 'Valid NBC TALK PAY user',
        public_key: response.public_key,
        wallet_address: response.public_key, // Alias para compatibilidad
        user_data: response.user_data
      };
      console.log(`✅ [validateNBCUser] User validated successfully:`, normalizedResponse);
      return normalizedResponse;
    } else {
      // Si no hay public_key, el usuario no existe
      const errorResponse: NBCUser = {
        success: false,
        message: response.message || 'This email is not registered with NBC TALK PAY'
      };
      console.log(`❌ [validateNBCUser] User not found:`, errorResponse);
      return errorResponse;
    }
    
  } catch (error: unknown) {
    console.error('❌ [validateNBCUser] Error occurred:', error);
    
    // Si falla el proxy y no estamos usando ya la llamada directa, intentar fallback
    if (API_CONFIG.useProxy && error instanceof Error && error.message.includes('Failed to fetch')) {
      console.log('🔄 [validateNBCUser] Proxy failed, trying direct call as fallback...');
      try {
        const requestBody = { identifier: identifier.trim() };
        const fallbackResponse = await makeRequest<NBCUser>('/get_wallet_by_user', {
          method: 'POST',
          body: JSON.stringify(requestBody),
        });
        console.log(`✅ [validateNBCUser] Fallback successful:`, fallbackResponse);
        return fallbackResponse;
      } catch (fallbackError: unknown) {
        console.error('❌ [validateNBCUser] Fallback also failed:', fallbackError);
      }
    }
    
    // Agregar información adicional sobre el error
    if (error instanceof Error) {
      console.error('❌ [validateNBCUser] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    }
    
    throw error;
  }
}

/**
 * Validar formato de email básico
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Obtener información de configuración para debugging
 */
export function getAPIConfig() {
  return {
    baseUrl: API_CONFIG.baseUrl,
    hasApiKey: !!API_CONFIG.apiKey,
    timeout: API_CONFIG.timeout,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Validar configuración del servicio
 */
export function validateAPIConfig(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!API_CONFIG.baseUrl) {
    errors.push('NEXT_PUBLIC_NBC_API_URL not configured');
  }

  if (!API_CONFIG.apiKey || API_CONFIG.apiKey === 'default_api_key') {
    errors.push('NEXT_PUBLIC_NBC_API_KEY not configured properly');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

//------------------------ Casino Partners Management ------------------------

/**
 * Realizar request HTTP para operaciones administrativas
 */
async function makeAdminRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  console.log(`🔐 [makeAdminRequest] Admin operation: ${endpoint}`);
  
  // Obtener token JWT del localStorage
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  
  const headers: Record<string, string> = {
    'X-Admin-API-Key': API_CONFIG.adminApiKey,
    'x-api-key': API_CONFIG.apiKey, // Casino endpoints necesitan ambas claves
    ...options.headers as Record<string, string>,
  };
  
  // Agregar Authorization header con JWT si existe
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log(`🔐 [makeAdminRequest] Added JWT token to request`);
  } else {
    console.warn(`⚠️ [makeAdminRequest] No JWT token found in localStorage`);
  }
  
  // Para FormData, no establecer Content-Type (el navegador lo hace automáticamente)
  const isFormData = options.body instanceof FormData;
  if (isFormData) {
    console.log('🔐 [makeAdminRequest] Detected FormData, removing Content-Type header');
    // Eliminar Content-Type para FormData - el navegador lo establecerá con boundary correcto
    delete headers['Content-Type'];
  }
  
  return makeRequest<T>(endpoint, {
    ...options,
    headers,
  });
}

/**
 * Listar todos los casino partners
 */
export async function listCasinoPartners(): Promise<ListCasinoPartnersResponse> {
  console.log('📋 [listCasinoPartners] Fetching casino partners list');
  
  try {
    const endpoint = '/api/admin/partners';
      
    const response = await makeAdminRequest<{ success: boolean; partners: CasinoPartner[]; count: number }>(endpoint, {
      method: 'GET',
    });
    
    // El backend Flask devuelve { success: true, partners: [...], count: N }
    // Si la respuesta es directamente un array, envolverlo en la estructura esperada
    let partnersData: CasinoPartner[] = [];
    let totalCount = 0;
    
    if (Array.isArray(response)) {
      // Si es un array directo (versión anterior del proxy)
      partnersData = response as unknown as CasinoPartner[];
      totalCount = partnersData.length;
    } else if (response.success && response.partners) {
      // Estructura completa del backend
      partnersData = response.partners;
      totalCount = response.count || partnersData.length;
    } else {
      console.warn('⚠️ [listCasinoPartners] Unexpected response structure:', response);
      partnersData = [];
    }
    
    // Procesar partners para agregar campos de UI
    const processedPartners = partnersData.map(partner => ({
      ...partner,
      masked_api_key: partner.api_key ? `${partner.api_key.substring(0, 8)}...${partner.api_key.substring(partner.api_key.length - 4)}` : 'N/A',
      status_display: partner.is_active ? 'Activo' as const : 'Inactivo' as const,
    }));
    
    console.log(`✅ [listCasinoPartners] Retrieved ${processedPartners.length} casino partners`);
    
    return {
      success: true,
      partners: processedPartners,
      total: totalCount,
    };
    
  } catch (error) {
    console.error('❌ [listCasinoPartners] Error:', error);
    return {
      success: false,
      partners: [],
      total: 0,
      error: error instanceof Error ? error.message : 'Error desconocido al obtener casino partners',
    };
  }
}

/**
 * Crear nuevo casino partner
 */
export async function createCasinoPartner(data: CreateCasinoPartnerRequest): Promise<CreateCasinoPartnerResponse> {
  console.log('➕ [createCasinoPartner] Creating casino partner:', data.name);
  
  try {
    const endpoint = '/api/admin/partners';
    
    // Preparar form data
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.webhook_url) formData.append('webhook_url', data.webhook_url);
    formData.append('is_active', String(data.is_active ?? true));
    
    const response = await makeAdminRequest<CreateCasinoPartnerResponse>(endpoint, {
      method: 'POST',
      headers: {
        // No establecer Content-Type para FormData, el navegador lo hace automáticamente
      },
      body: formData,
    });
    
    console.log('✅ [createCasinoPartner] Casino partner created successfully');
    return response;
    
  } catch (error) {
    console.error('❌ [createCasinoPartner] Error:', error);
    return {
      success: false,
      message: 'Error al crear casino partner',
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

/**
 * Obtener casino partner específico
 */
export async function getCasinoPartner(partnerId: string): Promise<GetCasinoPartnerResponse> {
  console.log(`🔍 [getCasinoPartner] Fetching partner: ${partnerId}`);
  
  try {
    const endpoint = `/api/admin/partners/${partnerId}`;
      
    const response = await makeAdminRequest<GetCasinoPartnerResponse>(endpoint, {
      method: 'GET',
    });
    
    // Procesar partner para UI si existe
    if (response.partner) {
      response.partner.masked_api_key = `${response.partner.api_key.substring(0, 8)}...${response.partner.api_key.substring(response.partner.api_key.length - 4)}`;
      response.partner.status_display = response.partner.is_active ? 'Activo' : 'Inactivo';
    }
    
    console.log('✅ [getCasinoPartner] Partner retrieved successfully');
    return response;
    
  } catch (error) {
    console.error('❌ [getCasinoPartner] Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al obtener casino partner',
    };
  }
}

/**
 * Actualizar casino partner
 */
export async function updateCasinoPartner(partnerId: string, data: UpdateCasinoPartnerRequest): Promise<UpdateCasinoPartnerResponse> {
  console.log(`✏️ [updateCasinoPartner] Updating partner: ${partnerId}`);
  
  try {
    const endpoint = `/api/admin/partners/${partnerId}`;
    
    // Preparar form data
    const formData = new FormData();
    if (data.name !== undefined) formData.append('name', data.name);
    if (data.webhook_url !== undefined) formData.append('webhook_url', data.webhook_url);
    if (data.is_active !== undefined) formData.append('is_active', String(data.is_active));
    
    const response = await makeAdminRequest<UpdateCasinoPartnerResponse>(endpoint, {
      method: 'PUT',
      body: formData,
    });
    
    // Procesar partner actualizado para UI
    if (response.partner) {
      response.partner.masked_api_key = `${response.partner.api_key.substring(0, 8)}...${response.partner.api_key.substring(response.partner.api_key.length - 4)}`;
      response.partner.status_display = response.partner.is_active ? 'Activo' : 'Inactivo';
    }
    
    console.log('✅ [updateCasinoPartner] Partner updated successfully');
    return response;
    
  } catch (error) {
    console.error('❌ [updateCasinoPartner] Error:', error);
    return {
      success: false,
      message: 'Error al actualizar casino partner',
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

/**
 * Eliminar (desactivar) casino partner
 */
export async function deleteCasinoPartner(partnerId: string): Promise<DeletePartnerResponse> {
  console.log(`🗑️ [deleteCasinoPartner] Deleting partner: ${partnerId}`);
  
  try {
    const endpoint = `/api/admin/partners/${partnerId}`;
      
    const response = await makeAdminRequest<DeletePartnerResponse>(endpoint, {
      method: 'DELETE',
    });
    
    console.log('✅ [deleteCasinoPartner] Partner deleted successfully');
    return response;
    
  } catch (error) {
    console.error('❌ [deleteCasinoPartner] Error:', error);
    return {
      success: false,
      message: 'Error al eliminar casino partner',
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

/**
 * Regenerar API key de casino partner
 */
export async function regenerateCasinoPartnerApiKey(partnerId: string): Promise<RegenerateApiKeyResponse> {
  console.log(`🔄 [regenerateCasinoPartnerApiKey] Regenerating API key for partner: ${partnerId}`);
  
  try {
    const endpoint = `/api/admin/partners/${partnerId}/regenerate-key`;
    const response = await makeAdminRequest<RegenerateApiKeyResponse>(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    
    console.log('✅ [regenerateCasinoPartnerApiKey] API key regenerated successfully');
    return response;
    
  } catch (error) {
    console.error('❌ [regenerateCasinoPartnerApiKey] Error:', error);
    return {
      success: false,
      message: 'Error al regenerar API key',
      error: error instanceof Error ? error.message : 'Error desconocido',
    };
  }
}

/**
 * Objeto de conveniencia para operaciones de casino partners
 */
export const casinoPartners = {
  list: listCasinoPartners,
  create: createCasinoPartner,
  get: getCasinoPartner,
  update: updateCasinoPartner,
  delete: deleteCasinoPartner,
  regenerateApiKey: regenerateCasinoPartnerApiKey,
};

// Exponer funciones de debug en desarrollo
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).nbcAPIDebug = {
    getConfig: getAPIConfig,
    validateConfig: validateAPIConfig,
    testValidation: validateNBCUser,
    casinoPartners: {
      list: listCasinoPartners,
      create: createCasinoPartner,
      get: getCasinoPartner,
      update: updateCasinoPartner,
      delete: deleteCasinoPartner,
      regenerateKey: regenerateCasinoPartnerApiKey,
    },
    checkEnvVars: () => ({
      NBC_PAY_API_URL: process.env.NEXT_PUBLIC_NBC_PAY_API_URL,
      NBC_PAY_API_KEY: process.env.NEXT_PUBLIC_NBC_PAY_API_KEY ? '***SET***' : 'NOT_SET',
      ADMIN_API_KEY: process.env.ADMIN_API_KEY ? '***SET***' : 'NOT_SET',
      hasConfig: !!process.env.NEXT_PUBLIC_NBC_PAY_API_URL,
    }),
  };
  console.log('🔧 NBC API Debug functions available at window.nbcAPIDebug');
  console.log('🔧 Check env vars with: window.nbcAPIDebug.checkEnvVars()');
  console.log('🔧 Test casino partners with: window.nbcAPIDebug.casinoPartners.list()');
}

/**
 * Regenerar API key de partner estándar
 */
export async function regeneratePartnerApiKey(partnerId: string): Promise<RegenerateApiKeyResponse> {
  console.log(`🔄 [regeneratePartnerApiKey] Regenerating API key for partner: ${partnerId}`);
  try {
    // 1. Intentar por proxy/ruta relativa
    const endpoint = `/api/admin/partners/${partnerId}/regenerate-key`;
    try {
      const response = await makeAdminRequest<RegenerateApiKeyResponse>(endpoint, {
        method: 'POST',
      });
      console.log('✅ [regeneratePartnerApiKey] API key regenerated successfully');
      return response;
    } catch (error) {
      console.error('❌ [regeneratePartnerApiKey] Proxy call failed, trying direct backend call...', error);
      // 2. Fallback: llamada directa al backend Flask
      const directUrl = `${API_CONFIG.baseUrl}/api/admin/partners/${partnerId}/regenerate-key`;
      const directResponse = await fetch(directUrl, {
        method: 'POST',
        headers: {
          'X-Admin-API-Key': API_CONFIG.adminApiKey,
          'x-api-key': API_CONFIG.apiKey,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        mode: 'cors',
        credentials: 'omit',
      });
      if (!directResponse.ok) {
        throw new Error(`HTTP ${directResponse.status}: ${directResponse.statusText}`);
      }
      const directData = await directResponse.json();
      console.log('✅ [regeneratePartnerApiKey] Direct call successful:', directData);
      return directData;
    }
  } catch (error) {
    console.error('❌ [regeneratePartnerApiKey] Error:', error);
    return {
      success: false,
      message: 'Error regenerating API key',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}